import { useState } from "react";
import { rfqCategories } from "@/mocks/factoryProfiles";
import type { FactoryProfile } from "@/mocks/factoryProfiles";

interface Props {
  preselectedFactory?: FactoryProfile | null;
  onClose: () => void;
}

export default function RFQModal({ preselectedFactory, onClose }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    country: "",
    category: preselectedFactory?.category ?? rfqCategories[0],
    productDescription: "",
    quantity: "",
    targetPrice: "",
    deliveryPort: "",
    deadline: "",
    requirements: "",
    sendToAll: !preselectedFactory,
  });

  const set = (key: keyof typeof form, val: string | boolean) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName || !form.contactName || !form.email || !form.productDescription || !form.quantity) {
      setError("Please fill in all required fields.");
      return;
    }
    if (form.productDescription.length > 500) {
      setError("Product description must be 500 characters or less.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const body = new URLSearchParams({
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        country: form.country,
        category: form.category,
        productDescription: form.productDescription,
        quantity: form.quantity,
        targetPrice: form.targetPrice,
        deliveryPort: form.deliveryPort,
        deadline: form.deadline,
        requirements: form.requirements,
        targetFactory: preselectedFactory ? preselectedFactory.name : "All Verified Factories",
        sendToAll: form.sendToAll ? "Yes" : "No",
      });
      await fetch("https://readdy.ai/api/form/d7cof4skpdh0a4advmig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-extrabold text-gray-900 text-lg">Request for Quotation (RFQ)</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {preselectedFactory
                ? `Sending to: ${preselectedFactory.flag} ${preselectedFactory.name}`
                : "Send to 500+ verified factories simultaneously"}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center py-14 px-6 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mb-5">
              <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-2">RFQ Submitted!</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-sm">
              Your quotation request has been sent to {preselectedFactory ? preselectedFactory.name : "500+ verified factories"}. Expect responses within 24 hours.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 w-full max-w-sm mb-6 text-left">
              <p className="text-xs font-bold text-amber-700 mb-2">What happens next?</p>
              <ul className="flex flex-col gap-2">
                {[
                  "Factories review your requirements",
                  "You receive competitive quotes via email",
                  "Compare offers and negotiate directly",
                  "Place sample or bulk order with confidence",
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-5 h-5 flex items-center justify-center bg-amber-400 text-white rounded-full text-xs font-bold shrink-0">{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={onClose}
              className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-10 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
            {/* Send to all toggle */}
            {!preselectedFactory && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-amber-700">Broadcast to All Factories</p>
                  <p className="text-xs text-amber-600">Send your RFQ to 500+ verified factories at once</p>
                </div>
                <button
                  type="button"
                  onClick={() => set("sendToAll", !form.sendToAll)}
                  className={`w-12 h-6 rounded-full transition-colors cursor-pointer relative ${form.sendToAll ? "bg-amber-400" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.sendToAll ? "left-6" : "left-0.5"}`}></span>
                </button>
              </div>
            )}

            {/* Company info */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Company Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Company Name *</label>
                  <input
                    required
                    value={form.companyName}
                    onChange={e => set("companyName", e.target.value)}
                    placeholder="Your Company Ltd."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Contact Name *</label>
                  <input
                    required
                    value={form.contactName}
                    onChange={e => set("contactName", e.target.value)}
                    placeholder="John Smith"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Email *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={e => set("email", e.target.value)}
                    placeholder="john@company.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
                  <input
                    value={form.phone}
                    onChange={e => set("phone", e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Country / Region</label>
                  <input
                    value={form.country}
                    onChange={e => set("country", e.target.value)}
                    placeholder="United States"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Product requirements */}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Product Requirements</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Category *</label>
                  <select
                    value={form.category}
                    onChange={e => set("category", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                  >
                    {rfqCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">
                    Product Description * <span className="text-gray-400 font-normal">({form.productDescription.length}/500)</span>
                  </label>
                  <textarea
                    required
                    value={form.productDescription}
                    onChange={e => {
                      if (e.target.value.length <= 500) set("productDescription", e.target.value);
                    }}
                    placeholder="Describe the product you need: specifications, model, wattage, voltage, color, size, etc."
                    rows={3}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Quantity Required *</label>
                  <input
                    required
                    value={form.quantity}
                    onChange={e => set("quantity", e.target.value)}
                    placeholder="e.g. 100 units, 1 container"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Target Price (USD)</label>
                  <input
                    value={form.targetPrice}
                    onChange={e => set("targetPrice", e.target.value)}
                    placeholder="e.g. $80/unit or $50,000 total"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Delivery Port</label>
                  <input
                    value={form.deliveryPort}
                    onChange={e => set("deliveryPort", e.target.value)}
                    placeholder="e.g. Los Angeles, Rotterdam"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Required By Date</label>
                  <input
                    type="date"
                    value={form.deadline}
                    onChange={e => set("deadline", e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors cursor-pointer"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">
                    Additional Requirements <span className="text-gray-400 font-normal">({form.requirements.length}/500)</span>
                  </label>
                  <textarea
                    value={form.requirements}
                    onChange={e => {
                      if (e.target.value.length <= 500) set("requirements", e.target.value);
                    }}
                    placeholder="Certifications needed, custom branding, packaging requirements, payment terms, etc."
                    rows={2}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-400 hover:bg-amber-500 disabled:bg-amber-200 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Submitting...
                </span>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-2"></i>
                  Submit RFQ to {preselectedFactory ? preselectedFactory.name : "500+ Factories"}
                </>
              )}
            </button>
            <p className="text-center text-xs text-gray-400">Free to submit · No spam · Factories respond within 24 hours</p>
          </form>
        )}
      </div>
    </div>
  );
}
